// ─────────────────────────────────────────────
// KLOTHIQ — Cash Adjustment Modal
// ─────────────────────────────────────────────

import UserGuide from '@/components/help/UserGuide';
import { Input, Select } from '@/components/ui/FormControls';
import DatePicker from '@/components/common/DatePicker';
import { useState, useEffect } from 'react';
import { X, IndianRupee, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useRecordAdjustment } from '@/hooks/useAccounts';
import { useCashAccounts } from '@/hooks/useCash';
import { parseRpcError, toISODate } from '@/utils/cashbookCalc';

interface AdjustmentEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function AdjustmentEntryModal({
  isOpen,
  onClose,
}: AdjustmentEntryModalProps) {
  const { data: accounts, isLoading: loadingAccounts } = useCashAccounts();
  const recordAdjustment = useRecordAdjustment();

  const [accountId, setAccountId] = useState('');
  const [direction, setDirection] = useState<'income' | 'expense'>('income');
  const [particular, setParticular] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(() => toISODate(new Date()));
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setDate(toISODate(new Date()));
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && accounts && accounts.length > 0 && !accountId) {
      const defaultAcc = accounts[0];
      if (defaultAcc) setAccountId(defaultAcc.id);
    }
  }, [isOpen, accounts, accountId]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accountId) {
      toast.error('Please select an account');
      return;
    }
    const numAmount = parseFloat(amount);
    if (!numAmount || numAmount <= 0) {
      toast.error('Please enter a valid positive adjustment amount');
      return;
    }
    if (particular.trim().length < 3) {
      toast.error('Please enter the adjustment reason (at least 3 characters)');
      return;
    }
    if (!date) {
      toast.error('Date is required');
      return;
    }
    const todayStr = toISODate(new Date());
    if (date > todayStr) {
      toast.error('Date cannot be in the future');
      return;
    }

    setSubmitting(true);
    try {
      await recordAdjustment.mutateAsync({
        account_id: accountId,
        amount: numAmount,
        direction,
        particular: particular.trim(),
        date,
        notes: notes.trim() || null,
      });
      toast.success('Cash adjustment recorded successfully');
      onClose();
      // Reset form
      setAmount('');
      setParticular('');
      setNotes('');
    } catch (err) {
      const parsed = parseRpcError(err);
      toast.error(parsed.message);
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    'w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-xs" data-field-guide-scope>
      <div className="obsidian-panel relative w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-xl animate-in fade-in zoom-in-95">
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="absolute right-4 top-4 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="flex items-start justify-between gap-3 pr-8">
          <h2 className="text-lg font-semibold text-foreground">Record Cash Adjustment</h2>
          <UserGuide compact />
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          Correct cash discrepancies, tally adjustments or balance revisions
        </p>

        <form onSubmit={handleSubmit} noValidate className="mt-5 space-y-4">
          {/* Direction toggle */}
          <div className="flex rounded-md border border-border bg-muted/30 p-0.5">
            <button
              type="button"
              onClick={() => setDirection('income')}
              className={`flex-1 rounded py-1.5 text-xs font-medium transition-colors ${
                direction === 'income'
                  ? 'bg-background text-emerald-600 shadow-xs dark:text-emerald-400'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              + Cash Increase (In)
            </button>
            <button
              type="button"
              onClick={() => setDirection('expense')}
              className={`flex-1 rounded py-1.5 text-xs font-medium transition-colors ${
                direction === 'expense'
                  ? 'bg-background text-rose-600 shadow-xs dark:text-rose-400'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              - Cash Decrease (Out)
            </button>
          </div>

          {/* Account */}
          <div className="space-y-1.5">
            <label htmlFor="adj-account" className="text-xs font-medium text-foreground">
              Account <span className="text-destructive">*</span>
            </label>
            <Select
              id="adj-account"
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              className={inputCls}
              disabled={loadingAccounts || submitting}
              helpLabel="Account *"
            >
              <option value="">Select account</option>
              {accounts?.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} — Balance: ₹{Number(a.current_balance).toLocaleString('en-IN')}
                </option>
              ))}
            </Select>
          </div>

          {/* Amount */}
          <div className="space-y-1.5">
            <label htmlFor="adj-amount" className="text-xs font-medium text-foreground">
              Adjustment Amount (₹) <span className="text-destructive">*</span>
            </label>
            <div className="relative">
              <IndianRupee className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="adj-amount"
                type="number"
                min="0.01"
                step="0.01"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className={`${inputCls} pl-8`}
                disabled={submitting}
                helpLabel="Adjustment Amount (₹) *"
              />
            </div>
          </div>

          {/* Particular */}
          <div className="space-y-1.5">
            <label htmlFor="adj-particular" className="text-xs font-medium text-foreground">
              Reason / Description <span className="text-destructive">*</span>
            </label>
            <Input
              id="adj-particular"
              type="text"
              placeholder="e.g. End of day cash drawer discrepancy"
              value={particular}
              onChange={(e) => setParticular(e.target.value)}
              className={inputCls}
              disabled={submitting}
              helpLabel="Reason / Description *"
            />
          </div>

          {/* Date */}
          <div className="space-y-1.5">
            <label htmlFor="adj-date" className="text-xs font-medium text-foreground">
              Date <span className="text-destructive">*</span>
            </label>
            <DatePicker
              clearable={false}
              aria-label="Date"
              id="adj-date"
              value={date}
              onChange={(nextDate) => setDate(nextDate ?? '')}
              disabled={submitting}
            />
          </div>

          {/* Notes */}
          <div className="space-y-1.5">
            <label htmlFor="adj-notes" className="text-xs font-medium text-foreground">
              Notes (Optional)
            </label>
            <Input
              id="adj-notes"
              type="text"
              placeholder="Additional explanation"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className={inputCls}
              disabled={submitting}
              helpLabel="Notes (Optional)"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2.5 pt-3">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="rounded-md border border-border bg-background px-4 py-2 text-xs font-medium text-foreground hover:bg-muted disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
            >
              {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Save Adjustment
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
